/**
 * The illuminance layer's server state: computed work-plane grids
 * (POST /v1/lighting/illuminance -- fresh, sub-second raster math).
 * Fetched only while the layer is toggled on.
 */
import { useQuery } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface RoomIlluminance {
  scope: string;
  room_name: string | null;
  fixtures: number;
  points: [number, number, number][]; // (x, y, lux) on the work plane
  min_lux: number;
  avg_lux: number;
  worst_point: [number, number];
}

// The fixed color-scale reference (lux): 300 is the IES office target,
// so the same color means the same light level in every project.
export const LUX_SCALE_MAX = 300;

export function useIlluminance(projectId: string, enabled: boolean) {
  return useQuery<RoomIlluminance[]>({
    queryKey: ['illuminance', projectId],
    enabled,
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/lighting/illuminance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!res.ok) throw new Error(`illuminance: ${res.status}`);
      return (await res.json()).rooms ?? [];
    },
  });
}
